/**
 * WelcomeBanner - renders a single welcome-page content block.
 *
 * The block content is data, authored by staff and stored on
 * `Tenant.settings["welcome_page"]`. This component is purely presentational
 * and is shared between the public consumer landing page and the staff setup
 * editor's live preview, so the preview is always faithful to production.
 */

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/components/ui/button'

export type WelcomeBlockType =
  | 'COVER'
  | 'OVERLAY'
  | 'TEXT_ONLY'
  | 'SPOTLIGHT'
  | 'EXAMPLE_PRODUCT'

export type WelcomeBlockAlignment = 'LEFT' | 'RIGHT'

export type WelcomeBlockCtaType = 'INTERNAL_REGISTRATION' | 'EXTERNAL_LINK'

export interface WelcomeBlockImage {
  uuid: string
  file_path: string | null
  file_name: string | null
}

export interface WelcomePageContentBlock {
  content_type: WelcomeBlockType
  block_title?: string | null
  block_body?: string | null
  block_image?: WelcomeBlockImage | null
  block_cta_type?: WelcomeBlockCtaType | null
  block_cta_content?: string | null
  block_cta_url?: string | null
  content_alignment?: WelcomeBlockAlignment | null
}

interface WelcomeBannerProps {
  block: WelcomePageContentBlock
  orgName: string
  /** Image URL for the highlighted example product (EXAMPLE_PRODUCT block). */
  exampleProductImageUrl?: string | null
  /**
   * Called when an INTERNAL_REGISTRATION CTA is clicked. When omitted (e.g. in
   * the staff preview) the CTA renders but does nothing on click.
   */
  onRegister?: () => void
  /**
   * WPS-138 #1: whether the tenant offers consumer self-registration. When
   * `false` (a tenant opted out, e.g. OC at go-live) the INTERNAL_REGISTRATION
   * CTA is hidden entirely. Defaults to `true` so the staff preview and every
   * other caller are unchanged.
   */
  registrationEnabled?: boolean
}

const IMAGE_PLACEHOLDER =
  'flex items-center justify-center bg-[var(--bg-subtle)] text-[12px] text-[var(--ink-3)]'

function BlockCta({
  block,
  onRegister,
  registrationEnabled = true,
}: {
  block: WelcomePageContentBlock
  onRegister?: () => void
  registrationEnabled?: boolean
}) {
  if (!block.block_cta_type || !block.block_cta_content) return null

  if (block.block_cta_type === 'EXTERNAL_LINK') {
    return (
      <a
        href={block.block_cta_url || '#'}
        target="_blank"
        rel="noreferrer"
        className="inline-block"
      >
        <Button size="lg">{block.block_cta_content}</Button>
      </a>
    )
  }

  // INTERNAL_REGISTRATION - hidden entirely for tenants that do not offer
  // consumer self-registration (WPS-138 #1).
  if (!registrationEnabled) return null
  return (
    <Button size="lg" onClick={onRegister}>
      {block.block_cta_content}
    </Button>
  )
}

export function WelcomeBanner({
  block,
  orgName,
  exampleProductImageUrl,
  onRegister,
  registrationEnabled = true,
}: WelcomeBannerProps) {
  const imageUrl = block.block_image?.file_path || null
  const alignRight = block.content_alignment === 'RIGHT'

  if (block.content_type === 'TEXT_ONLY') {
    return (
      <div className="flex flex-col items-start gap-6">
        {block.block_title && (
          <h2 className="font-display text-[28px] leading-tight text-[var(--ink)] md:text-[36px]">
            {block.block_title}
          </h2>
        )}
        {block.block_body && (
          <p className="max-w-3xl whitespace-pre-line text-[16px] leading-relaxed text-[var(--ink-2)] md:text-[18px]">
            {block.block_body}
          </p>
        )}
        <BlockCta
          block={block}
          onRegister={onRegister}
          registrationEnabled={registrationEnabled}
        />
      </div>
    )
  }

  if (block.content_type === 'COVER') {
    return (
      <div className="max-h-[420px] w-full overflow-hidden rounded-3xl">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={block.block_image?.file_name || ''}
            className="max-h-[420px] w-full object-cover"
          />
        ) : (
          <div className={cn(IMAGE_PLACEHOLDER, 'h-[280px] w-full rounded-3xl')}>
            No cover image
          </div>
        )}
      </div>
    )
  }

  if (block.content_type === 'OVERLAY') {
    return (
      <div className="relative h-[420px] w-full overflow-hidden rounded-3xl">
        {imageUrl ? (
          <img
            src={imageUrl}
            alt={block.block_image?.file_name || ''}
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className={cn(IMAGE_PLACEHOLDER, 'absolute inset-0')}>
            No overlay image
          </div>
        )}
        <div className="absolute inset-0 bg-black/40" />
        <div
          className={cn(
            'absolute inset-0 flex flex-col justify-center gap-6 p-8 md:p-12',
            alignRight ? 'items-end text-right' : 'items-start text-left',
          )}
        >
          <div className={cn('flex max-w-xl flex-col gap-5', alignRight && 'items-end')}>
            {block.block_title && (
              <h2 className="font-display text-[34px] leading-tight text-white md:text-[48px]">
                {block.block_title}
              </h2>
            )}
            {block.block_body && (
              <p className="whitespace-pre-line text-[16px] leading-relaxed text-white/90 md:text-[18px]">
                {block.block_body}
              </p>
            )}
            <BlockCta
          block={block}
          onRegister={onRegister}
          registrationEnabled={registrationEnabled}
        />
          </div>
        </div>
      </div>
    )
  }

  if (block.content_type === 'SPOTLIGHT') {
    const image = imageUrl ? (
      <img
        src={imageUrl}
        alt={block.block_image?.file_name || ''}
        className="h-[320px] w-full overflow-hidden rounded-3xl object-cover"
      />
    ) : (
      <div className={cn(IMAGE_PLACEHOLDER, 'h-[320px] w-full rounded-3xl')}>
        No image
      </div>
    )

    return (
      <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
        {!alignRight && image}
        <div className="flex flex-col gap-5">
          {block.block_title && (
            <h2 className="font-display text-[28px] leading-tight text-[var(--ink)] md:text-[36px]">
              {block.block_title}
            </h2>
          )}
          {block.block_body && (
            <p className="whitespace-pre-line text-[16px] leading-relaxed text-[var(--ink-2)] md:text-[18px]">
              {block.block_body}
            </p>
          )}
          <BlockCta
          block={block}
          onRegister={onRegister}
          registrationEnabled={registrationEnabled}
        />
        </div>
        {alignRight && image}
      </div>
    )
  }

  if (block.content_type === 'EXAMPLE_PRODUCT') {
    return (
      <div className="grid grid-cols-1 items-center gap-10 md:grid-cols-2">
        <div className="h-[360px] w-full overflow-hidden rounded-3xl">
          {exampleProductImageUrl ? (
            <img
              src={exampleProductImageUrl}
              alt="Product"
              className="h-full w-full object-cover"
            />
          ) : (
            <div className={cn(IMAGE_PLACEHOLDER, 'h-full w-full rounded-3xl')}>
              No product image
            </div>
          )}
        </div>
        <div className="flex flex-col gap-6">
          <h2 className="font-display text-[30px] leading-tight text-[var(--ink)] md:text-[42px]">
            {block.block_title || `Register your ${orgName} product today`}
          </h2>
          <p className="text-[16px] leading-relaxed text-[var(--ink-2)] md:text-[18px]">
            {block.block_body ||
              `${orgName} has partnered with iWarranty to give you convenience and peace of mind with your purchase.`}
          </p>
          <BlockCta
          block={block}
          onRegister={onRegister}
          registrationEnabled={registrationEnabled}
        />
        </div>
      </div>
    )
  }

  return null
}
